import { Add, AssignmentInd, Dashboard, Download, Edit, ManageSearch } from "@mui/icons-material";
import { Button, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { getDownload } from "../../components/form/info-form-modal/InfoFormModal";
import RiskAssignModal from "../../components/risk-assessment/RiskAssignModal";
import RiskSpinner from "../../components/risk-assessment/RiskSpinner";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskAssessmentHistoryDTO } from "../../types/RiskAssessmentDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './RiskAssessmentHistory.scss';

const NO_POSITIONS = 'Nenhum (somente novas datas)';
const IN_PROGRESS = 'Em edição';

const RiskAssessmentHistory: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [history, setHistory] = useState<RiskAssessmentHistoryDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [company, setCompany] = useState('');
	const [downloading, setDownloading] = useState<number | null>(null);
	const [assigning, setAssigning] = useState<RiskAssessmentHistoryDTO | null>(null);
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const { showError } = useAlert();
	const service = new RiskAssessmentService();

	useEffect(() => {
		const loadHistory = async () => {
			const response = await service.getHistory();
			if (!response) {
				showError('Não foi possível carregar o histórico. Recarregue a página para tentar novamente.');
			}
			setHistory(response?.data || []);
			setLoading(false);
		};
		loadHistory();
		if (!!permissions.length) {
			verifyPermission(permissions, 'RISK_ASSESSMENT');
		}
	}, []);

	const term = company.trim().toLowerCase();
	const filtered = history.filter(item => !term
		|| `${item.companyName || ''} - ${item.companyId}`.toLowerCase().includes(term));

	return (
		<Fragment>
			<AppBar
				modulo="Avaliação de riscos"
			/>
			<div className="risk-assessment-history">
				<div className="top">
					<Typography variant="h6">Avaliação de riscos</Typography>
					<div className="acoes">
						<Button
							variant="contained"
							color="success"
							startIcon={<Add/>}
							onClick={() => navigate('/avaliacao-riscos/nova')}
						>
							Nova avaliação de risco
						</Button>
						<Button
							variant="contained"
							color="info"
							startIcon={<Dashboard/>}
							onClick={() => navigate('/avaliacao-riscos/painel')}
						>
							Painel
						</Button>
					</div>
				</div>
				<div className="filtros">
					<TextField
						size="small"
						label="Empresa"
						placeholder="Nome ou código"
						value={company}
						onChange={(event) => setCompany(event.target.value)}
					/>
				</div>
				{loading
					? getLoading()
					: !filtered.length
						? <div className="sem-execucoes">
							<Typography variant="h6">
								{history.length
									? 'Nenhuma avaliação encontrada para o filtro informado.'
									: 'Nenhuma avaliação gerada até o momento.'}
							</Typography>
						</div>
						: <div className="tabela">
							<div className="header-tabela">
								<p>Atualizada em</p>
								<p>Situação</p>
								<p>Responsável e etapa</p>
								<p>Empresa</p>
								<p>Cargos manipulados</p>
								<p className="acao">Ações</p>
							</div>
							<div className="body-table">
								{filtered.map(item => {
									const draft = item.status === 'EM_EDICAO';
									const responsible = draft ? item.responsibleName || item.responsibleUsername || item.name : item.name;
									const positions = draft ? IN_PROGRESS : item.positions || NO_POSITIONS;
									return (
										<div className="linha-empresa" key={item.id}>
											<p title={`Iniciada em ${item.createdAt}`}>{item.updatedAt}</p>
											<div>
												<Chip
													size="small"
													variant="outlined"
													color={draft ? 'warning' : 'success'}
													label={draft ? IN_PROGRESS : 'Finalizada'}
												/>
											</div>
											<div className="responsavel">
												<p title={responsible}>{responsible}</p>
												{draft && !!item.stage && <span title={item.stage}>Etapa: {item.stage}</span>}
											</div>
											<p title={`${item.companyName} - ${item.companyId}`}>{item.companyName} - {item.companyId}</p>
											<p title={positions}>{positions}</p>
											<div className="acao">
												{draft
													? <Fragment>
														{item.canEdit && (
															<Tooltip title="Continuar editando">
																<IconButton onClick={() => navigate(`/avaliacao-riscos/nova/${item.id}`)}>
																	<Edit/>
																</IconButton>
															</Tooltip>
														)}
														{item.canAssign && (
															<Tooltip title="Alterar responsável e etapa">
																<IconButton onClick={() => setAssigning(item)}>
																	<AssignmentInd/>
																</IconButton>
															</Tooltip>
														)}
														{!item.canEdit && !item.canAssign && (
															<span className="bloqueada" title={`Em edição por ${responsible}`}>Em edição por {responsible}</span>
														)}
													</Fragment>
													: <Fragment>
														<Tooltip title={item.hasChanges ? 'Ver alterações' : 'Alterações não registradas (avaliação anterior a este recurso)'}>
															<span>
																<IconButton disabled={!item.hasChanges} onClick={() => navigate(`/avaliacao-riscos/historico/${item.id}`)}>
																	<ManageSearch/>
																</IconButton>
															</span>
														</Tooltip>
														<Tooltip title="Baixar planilha gerada">
															<IconButton disabled={downloading === item.id} onClick={() => download(item)}>
																{downloading === item.id ? <RiskSpinner size={20}/> : <Download/>}
															</IconButton>
														</Tooltip>
													</Fragment>
												}
											</div>
										</div>
									);
								})}
							</div>
						</div>
				}
			</div>
			<RiskAssignModal
				item={assigning}
				onClose={() => setAssigning(null)}
				onAssigned={(updated) => {
					setHistory(previous => previous.map(item => item.id === updated.id ? updated : item));
					setAssigning(null);
				}}
			/>
		</Fragment>
	);

	async function download(item: RiskAssessmentHistoryDTO) {
		setDownloading(item.id);
		const response = await service.downloadHistoryFile(item.id);
		setDownloading(null);
		if (!response) {
			showError('Não foi possível baixar a planilha.');
			return;
		}
		getDownload(response, item.fileName.replace(/\.xlsx$/i, ''));
	}
};

export default RiskAssessmentHistory;
